/**
 * The demo island: a small world MC Planner makes up itself, block by block — no Minecraft files, nothing from
 * Mojang. It is the live backdrop behind the setup wizard and the "Try the demo island" sandbox.
 *
 * `demoChunk()` builds a chunk the way the world reader decodes one from a region file (ChunkData with sections,
 * block palettes, biomes and heightmaps), so the normal chunk mesher and map renderer draw it: real 3D blocks with
 * face shading, ambient occlusion and biome colours, the map from the same blocks. The blocks are vanilla block
 * states (grass_block, oak_log[axis=y], oak_stairs[facing=north,…]) drawn with MC Planner's own built-in textures
 * (lib/demoBlocks.ts), or with the player's own game textures when their game file is loaded.
 *
 * The island: rolling plains around a village site (flattened ground for the example plan), oak and birch forests,
 * a spruce taiga below a mountain ridge in the north-west with stone cliffs, ores and snowy peaks, a river in the
 * east, beaches and the sea. Of the example plan, the Smithy ("done") stands built and the Market square ("in
 * progress") is half paved; everything else is still open ground, waiting for its plan.
 *
 * Pure functions (no DOM) — run in the world workers (engine/world.worker.ts), the demo worker and Node tests.
 */
import type { PlanObject } from "@mcp/plan";
import { resolveBlockName, type ChunkData, type DimensionInfo, type NameRegistry, type Section, type StateRegistry } from "@mcp/world";

/** 2 × 2 regions (1024 × 1024 blocks) around the origin: the island plus a ring of sea */
export const DEMO_REGIONS: [number, number][] = [[-1, -1], [0, -1], [-1, 0], [0, 0]];
/** the island's only dimension (no files: the world workers make its chunks) */
export const DEMO_DIM: DimensionInfo = { id: "minecraft:overworld", dir: "", regions: DEMO_REGIONS.map(([rx, rz]) => ({ rx, rz, path: `region/r.${rx}.${rz}.mca` })) };
/** the sky behind the backdrop (its fog fades the far land into it, so the map has no visible edge) */
export const DEMO_SKY: [number, number, number] = [164, 196, 238];
/** @deprecated the old flat map's sea colour; kept for the world thumbnail's open sea */
export const DEEP_SEA: [number, number, number] = [30, 64, 150];
export const DEMO_NAME = "Demo island";
export const DEMO_ROOM = "demo-island-v1";
/** y of the highest water block of the sea (the first air above it is SEA + 1, like Minecraft's sea level 63) */
export const SEA = 62;

// ---------- noise ----------

function hash(x: number, z: number, seed: number) {
  let h = Math.imul(x, 374761393) ^ Math.imul(z, 668265263) ^ Math.imul(seed, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function hash3(x: number, y: number, z: number, seed: number) {
  return hash(x + Math.imul(y, 1999), z - Math.imul(y, 2971), seed);
}

function noise(x: number, z: number, seed: number) {
  const xi = Math.floor(x), zi = Math.floor(z);
  const xf = x - xi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = zf * zf * (3 - 2 * zf);
  const a = hash(xi, zi, seed), b = hash(xi + 1, zi, seed), c = hash(xi, zi + 1, seed), d = hash(xi + 1, zi + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fbm(x: number, z: number, octaves: number, seed: number) {
  let sum = 0, amp = 1, norm = 0, f = 1;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * f, z * f, seed + i * 17) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2;
  }
  return sum / norm;
}

const smooth = (e0: number, e1: number, x: number) => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
const mix = (a: number, b: number, t: number) => a + (b - a) * t;

// ---------- terrain ----------

/** where the village sits (flattened ground) */
const TOWN = { x: 10, z: 0, r: 150 };

type Biome = "ocean" | "deep_ocean" | "beach" | "river" | "plains" | "forest" | "birch_forest" | "taiga" | "windswept_hills" | "snowy_slopes";

interface Column {
  /** y of the top solid block (water above it up to SEA when g < SEA) */
  g: number;
  biome: Biome;
  /** 0 … 1: how much this column belongs to the flattened village ground */
  town: number;
  /** 0 … 1: river bed */
  river: number;
}

const BIOMES: Biome[] = ["ocean", "deep_ocean", "beach", "river", "plains", "forest", "birch_forest", "taiga", "windswept_hills", "snowy_slopes"];

/** columns already worked out, per region (each worker keeps a few) */
const regionCols = new Map<string, { g: Int16Array; biome: Uint8Array; town: Uint8Array; river: Uint8Array; known: Uint8Array }>();

function column(x: number, z: number): Column {
  const key = `${x >> 9},${z >> 9}`;
  let r = regionCols.get(key);
  if (!r) {
    r = { g: new Int16Array(512 * 512), biome: new Uint8Array(512 * 512), town: new Uint8Array(512 * 512), river: new Uint8Array(512 * 512), known: new Uint8Array(512 * 512) };
    regionCols.set(key, r);
    while (regionCols.size > 6) regionCols.delete(regionCols.keys().next().value as string);
  }
  const i = (z & 511) * 512 + (x & 511);
  if (!r.known[i]) {
    const c = computeColumn(x, z);
    r.g[i] = c.g; r.biome[i] = BIOMES.indexOf(c.biome); r.town[i] = Math.round(c.town * 255); r.river[i] = Math.round(c.river * 255); r.known[i] = 1;
    return c;
  }
  return { g: r.g[i], biome: BIOMES[r.biome[i]], town: r.town[i] / 255, river: r.river[i] / 255 };
}

function computeColumn(x: number, z: number): Column {
  const ang = Math.atan2(z, x);
  const dist = Math.hypot(x, z);
  const radius = 330 + (fbm(Math.cos(ang) * 2 + 7, Math.sin(ang) * 2 + 3, 3, 5) - 0.5) * 220;
  if (dist > radius + 40) {
    const g = Math.round(SEA - 16 - fbm(x / 60, z / 60, 2, 31) * 8);
    return { g, biome: "deep_ocean", town: 0, river: 0 };
  }
  const land = 1 - smooth(radius - 70, radius + 10, dist);
  const hills = fbm(x / 160, z / 160, 5, 1);
  // a ridge of mountains in the north-west, with a rougher face
  const ridgeF = smooth(0.05, 0.62, (-x - z) / 520);
  const ridge = ridgeF > 0 ? ridgeF * Math.pow(fbm(x / 90 + 40, z / 90, 4, 9), 1.3) * 100 + ridgeF * (fbm(x / 23, z / 23, 2, 15) - 0.5) * 10 : 0;
  let h = SEA - 14 + land * (18 + hills * 26 + ridge);
  // flat ground for the village
  // (a wobbly edge: perfect circles would leave rings of one-block steps around the village)
  const townD = Math.hypot(x - TOWN.x, z - TOWN.z) + (fbm(x / 45, z / 45, 3, 33) - 0.5) * 90;
  const town = (1 - smooth(TOWN.r * 0.5, TOWN.r * 1.05, townD)) * land;
  h = mix(h, SEA + 5 + hills * 3, town);
  // small bumps, so the one-block steps of the slopes break up into patches instead of long contour lines
  h += (fbm(x / 9, z / 9, 2, 61) - 0.5) * 3.4 * (1 - town * 0.9) * land;
  // a winding river across the island, east of the village
  const bend = (fbm(x / 240, z / 240, 3, 21) - 0.5) * 220;
  const riverD = Math.abs(x - z * 0.35 - bend - 215);
  const river = 1 - smooth(3, 8, riverD);
  if (river > 0 && h > SEA - 3) h = mix(h, SEA - 3, river * (h > SEA + 30 ? 0.4 : 1));
  const g = Math.round(h);

  let biome: Biome;
  if (g < SEA - 1 && river > 0.2) biome = "river";
  else if (g < SEA - 6) biome = land < 0.5 ? "deep_ocean" : "ocean";
  else if (g < SEA) biome = "ocean";
  else if (g <= SEA + 2 && river < 0.1) biome = "beach";
  else if (g >= SEA + 66) biome = "snowy_slopes";
  else if (ridgeF > 0.3 && g >= SEA + 34) biome = "windswept_hills";
  else if (ridgeF > 0.12 && fbm(x / 80 - 9, z / 80 + 5, 3, 29) > 0.47 && town < 0.2) biome = "taiga";
  else if (fbm(x / 70 + 13, z / 70 - 4, 3, 3) > 0.56 && town < 0.4) biome = fbm(x / 110 - 31, z / 110 + 17, 3, 11) > 0.56 ? "birch_forest" : "forest";
  else biome = "plains";
  return { g, biome, town, river };
}

/** Ground height of the demo island at a block: y of the first air above the terrain (the sea surface over water). */
export function demoHeight(x: number, z: number) {
  const c = column(Math.round(x), Math.round(z));
  return Math.max(c.g, SEA) + 1;
}

// ---------- blocks ----------

const BLOCKS = {
  air: ["minecraft:air"],
  stone: ["minecraft:stone"],
  granite: ["minecraft:granite"],
  diorite: ["minecraft:diorite"],
  andesite: ["minecraft:andesite"],
  coal: ["minecraft:coal_ore"],
  iron: ["minecraft:iron_ore"],
  copper: ["minecraft:copper_ore"],
  dirt: ["minecraft:dirt"],
  grass: ["minecraft:grass_block", { snowy: "false" }],
  grassSnowy: ["minecraft:grass_block", { snowy: "true" }],
  sand: ["minecraft:sand"],
  sandstone: ["minecraft:sandstone"],
  gravel: ["minecraft:gravel"],
  clay: ["minecraft:clay"],
  water: ["minecraft:water", { level: "0" }],
  snow: ["minecraft:snow", { layers: "1" }],
  snowBlock: ["minecraft:snow_block"],
  shortGrass: ["minecraft:short_grass"],
  poppy: ["minecraft:poppy"],
  dandelion: ["minecraft:dandelion"],
  cornflower: ["minecraft:cornflower"],
  daisy: ["minecraft:oxeye_daisy"],
  cane: ["minecraft:sugar_cane", { age: "0" }],
  pumpkin: ["minecraft:pumpkin"],
  oakLog: ["minecraft:oak_log", { axis: "y" }],
  oakLogX: ["minecraft:oak_log", { axis: "x" }],
  oakLeaves: ["minecraft:oak_leaves", { distance: "1", persistent: "false", waterlogged: "false" }],
  birchLog: ["minecraft:birch_log", { axis: "y" }],
  birchLeaves: ["minecraft:birch_leaves", { distance: "1", persistent: "false", waterlogged: "false" }],
  spruceLog: ["minecraft:spruce_log", { axis: "y" }],
  spruceLeaves: ["minecraft:spruce_leaves", { distance: "1", persistent: "false", waterlogged: "false" }],
  oakPlanks: ["minecraft:oak_planks"],
  sprucePlanks: ["minecraft:spruce_planks"],
  cobble: ["minecraft:cobblestone"],
  mossy: ["minecraft:mossy_cobblestone"],
  stoneBricks: ["minecraft:stone_bricks"],
  glass: ["minecraft:glass"],
  path: ["minecraft:dirt_path"],
  hay: ["minecraft:hay_block", { axis: "y" }],
  roofN: ["minecraft:spruce_stairs", { facing: "north", half: "bottom", shape: "straight", waterlogged: "false" }],
  roofS: ["minecraft:spruce_stairs", { facing: "south", half: "bottom", shape: "straight", waterlogged: "false" }],
  fenceNS: ["minecraft:oak_fence", { north: "true", south: "true", east: "false", west: "false", waterlogged: "false" }],
  fenceEW: ["minecraft:oak_fence", { north: "false", south: "false", east: "true", west: "true", waterlogged: "false" }],
  fencePost: ["minecraft:oak_fence", { north: "false", south: "false", east: "false", west: "false", waterlogged: "false" }],
} as const satisfies Record<string, readonly [string, Record<string, string>?]>;

type BlockName = keyof typeof BLOCKS;
type Ids = Record<BlockName, number>;

/** what a block becomes when the loaded game file doesn't know it (an older version): plants vanish, ores are stone */
const STAND_IN: Partial<Record<BlockName, BlockName>> = {
  shortGrass: "air", poppy: "air", dandelion: "air", cornflower: "air", daisy: "air", cane: "air", pumpkin: "air", snow: "air",
  fenceNS: "air", fenceEW: "air", fencePost: "air", hay: "oakPlanks", roofN: "sprucePlanks", roofS: "sprucePlanks",
  path: "dirt", grassSnowy: "grass", snowBlock: "stone", mossy: "cobble", clay: "sand", sandstone: "sand",
  coal: "stone", iron: "stone", copper: "stone", granite: "stone", diorite: "stone", andesite: "stone",
};

const idCache = new WeakMap<StateRegistry, Ids>();
/**
 * The island's blocks as state ids of a registry. `known`: which blocks the loaded game file has — a renamed block takes
 * its other name (grass ↔ short_grass), one it doesn't have at all takes its stand-in (no placeholder cubes).
 */
function ids(states: StateRegistry, known?: (name: string) => boolean): Ids {
  let r = idCache.get(states);
  if (!r) {
    r = {} as Ids;
    const entries = Object.entries(BLOCKS) as [BlockName, readonly [string, Record<string, string>?]][];
    for (const [k0] of entries) {
      // follow the stand-ins until the game knows the block (air in the end)
      let k: BlockName = k0;
      for (let i = 0; i < 4 && known && k !== "air" && !known(resolveBlockName(BLOCKS[k][0], known)); i++) k = STAND_IN[k] ?? "air";
      const [name, props] = BLOCKS[k] as readonly [string, Record<string, string>?];
      r[k0] = states.id(known ? resolveBlockName(name, known) : name, props ? { ...props } : undefined);
    }
    idCache.set(states, r);
  }
  return r;
}

/** every block state the island uses: "name" or "name[prop=value,…]" (for tests) */
export function demoBlockStates(): { name: string; props: Record<string, string> }[] {
  return Object.values(BLOCKS).map(([name, props]) => ({ name, props: props ? { ...props } : {} }));
}

// ---------- the chunk ----------

class Builder {
  readonly x0: number;
  readonly z0: number;
  readonly fromY: number;
  readonly toY: number;
  readonly buf: Uint16Array;
  constructor(cx: number, cz: number, fromY: number, toY: number) {
    this.x0 = cx * 16; this.z0 = cz * 16; this.fromY = fromY; this.toY = toY;
    this.buf = new Uint16Array(16 * 16 * (toY - fromY));
  }
  /** world coordinates; outside the chunk / height range: ignored */
  set(x: number, y: number, z: number, id: number) {
    const lx = x - this.x0, lz = z - this.z0;
    if (lx < 0 || lx > 15 || lz < 0 || lz > 15 || y < this.fromY || y >= this.toY) return;
    this.buf[((y - this.fromY) * 16 + lz) * 16 + lx] = id;
  }
  get(x: number, y: number, z: number) {
    const lx = x - this.x0, lz = z - this.z0;
    if (lx < 0 || lx > 15 || lz < 0 || lz > 15 || y < this.fromY || y >= this.toY) return -1;
    return this.buf[((y - this.fromY) * 16 + lz) * 16 + lx];
  }
  inside(x: number, z: number, pad = 0) {
    return x >= this.x0 - pad && x < this.x0 + 16 + pad && z >= this.z0 - pad && z < this.z0 + 16 + pad;
  }
}

/** A column's surface block, from its height and its neighbours' (steep → bare stone). */
function topBlock(c: Column, slope: number, B: Ids, x: number, z: number): number {
  if (c.g < SEA) {
    if (c.biome === "river") return hash(x, z, 41) < 0.3 ? B.clay : B.sand;
    const depth = SEA - c.g;
    if (depth <= 5) return hash(x >> 2, z >> 2, 43) < 0.15 ? B.gravel : B.sand;
    return fbm(x / 18, z / 18, 2, 45) > 0.55 ? B.gravel : B.sand;
  }
  if (c.biome === "beach") return B.sand;
  if (c.g >= SEA + 66) return slope >= 3 ? B.stone : c.g >= SEA + 76 ? B.snowBlock : B.grassSnowy;
  if (c.g >= SEA + 40 && slope >= 3) return B.stone;
  if (c.g >= SEA + 14 && slope >= 4) return B.stone;
  if (c.biome === "windswept_hills" && fbm(x / 9, z / 9, 2, 47) > 0.66) return B.gravel;
  if (c.river > 0.05 && c.g <= SEA + 1) return B.sand;
  return B.grass;
}

/** stone, or a vein of granite / diorite / andesite / ore at this block */
function rock(x: number, y: number, z: number, B: Ids): number {
  const n = noise(x / 9 + y * 0.37, z / 9 - y * 0.21, 51);
  if (n > 0.8) return B.andesite;
  if (n < 0.14) return B.granite;
  const m = noise(x / 11 - y * 0.29, z / 11 + y * 0.31, 53);
  if (m > 0.84) return B.diorite;
  const cell = hash3(x >> 2, y >> 2, z >> 2, 55);
  if (cell < 0.05) {
    const kind = cell < 0.03 ? B.coal : y < 90 && cell < 0.042 ? B.iron : B.copper;
    if (hash3(x, y, z, 57) < 0.55) return kind;
  }
  return B.stone;
}

const TREE_CELL = 5;

type TreeKind = "oak" | "birch" | "spruce";

/** the tree of a 5 × 5 cell, if it has one: where its trunk stands, on which ground, what kind */
function treeIn(gx: number, gz: number): { x: number; z: number; g: number; kind: TreeKind } | null {
  const x = gx * TREE_CELL + Math.floor(hash(gx, gz, 101) * TREE_CELL), z = gz * TREE_CELL + Math.floor(hash(gx, gz, 103) * TREE_CELL);
  const c = column(x, z);
  if (c.g <= SEA || c.town > 0.3 || c.river > 0.02 || c.g >= SEA + 64) return null;
  const p = c.biome === "forest" || c.biome === "birch_forest" ? 0.82 : c.biome === "taiga" ? 0.78 : c.biome === "plains" ? 0.035 : c.biome === "windswept_hills" ? 0.12 : 0;
  if (hash(gx, gz, 105) >= p) return null;
  // only on gentle, grassy ground
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (Math.abs(column(x + dx, z + dz).g - c.g) > 1) return null;
  const k = hash(gx, gz, 107);
  const kind: TreeKind = c.biome === "taiga" ? "spruce" : c.biome === "birch_forest" ? (k < 0.88 ? "birch" : "oak") : c.biome === "forest" ? (k < 0.72 ? "oak" : "birch") : c.biome === "windswept_hills" ? (k < 0.6 ? "spruce" : "oak") : "oak";
  return { x, z, g: c.g, kind };
}

/** a tree whose trunk stands at (x, z) on ground g */
function stampTree(b: Builder, B: Ids, x: number, z: number, g: number, kind: TreeKind) {
  const r = (i: number) => hash(x * 3 + i, z * 7 - i, 61 + i);
  const leaf = kind === "oak" ? B.oakLeaves : kind === "birch" ? B.birchLeaves : B.spruceLeaves;
  const log = kind === "oak" ? B.oakLog : kind === "birch" ? B.birchLog : B.spruceLog;
  const put = (px: number, py: number, pz: number) => {
    const cur = b.get(px, py, pz);
    if (cur === B.air || cur === B.shortGrass || cur === B.poppy || cur === B.dandelion || cur === B.cornflower || cur === B.daisy) b.set(px, py, pz, leaf);
  };
  if (kind === "spruce") {
    // a cone of rings, widening downwards in steps (1, 2, 1, 2, 3, 2, 3 …), a single block on the very top
    const h = 7 + Math.floor(r(1) * 5);
    const top = g + h;
    put(x, top + 1, z);
    for (let y = top; y > g + 2; y--) {
      const k = top - y;
      const rad = k === 0 ? 1 : Math.min(3, [1, 2, 1, 2, 3, 2][(k - 1) % 6] + (k > 7 ? 1 : 0));
      for (let dz = -rad; dz <= rad; dz++)
        for (let dx = -rad; dx <= rad; dx++) {
          const d = Math.abs(dx) + Math.abs(dz);
          if (d > rad + (rad > 1 ? 1 : 0)) continue;
          if (d === rad + 1 && r(10 + dx * 5 + dz + k * 11) < 0.4) continue;
          put(x + dx, y, z + dz);
        }
    }
    for (let y = g + 1; y < top; y++) b.set(x, y, z, log);
    return;
  }
  const h = (kind === "birch" ? 5 : 4) + Math.floor(r(1) * 3);
  const top = g + h;
  for (let y = top - 2; y <= top + 1; y++) {
    const rad = y <= top - 1 ? 2 : 1;
    for (let dz = -rad; dz <= rad; dz++)
      for (let dx = -rad; dx <= rad; dx++) {
        const corner = Math.abs(dx) === rad && Math.abs(dz) === rad;
        if (corner && (y === top + 1 || r(20 + dx * 3 + dz * 7 + y) < 0.5)) continue;
        if (y === top + 1 && Math.abs(dx) + Math.abs(dz) > 1) continue;
        put(x + dx, y, z + dz);
      }
  }
  for (let y = g + 1; y <= top; y++) b.set(x, y, z, log);
}

// ---------- the village: what of the example plan already stands ----------

/** the Smithy (status "done") on its plot of the example plan: x 26…37, z 14…23 */
const SMITHY = { x0: 26, x1: 37, z0: 14, z1: 23 };
/** the Market square (status "in progress"): x −18…17, z −16…15, paved from the west up to about here */
const SQUARE = { x0: -18, x1: 17, z0: -16, z1: 15, done: 3 };

let smithyFloor: number | null = null;
/** floor y of the Smithy: the ground in the middle of its plot */
function smithyY() {
  smithyFloor ??= column((SMITHY.x0 + SMITHY.x1) >> 1, (SMITHY.z0 + SMITHY.z1) >> 1).g;
  return smithyFloor;
}

function stampSmithy(b: Builder, B: Ids) {
  if (!overlaps(b, SMITHY.x0 - 1, SMITHY.x1 + 2, SMITHY.z0 - 1, SMITHY.z1 + 7)) return;
  const F = smithyY();
  const { x0, x1, z0, z1 } = SMITHY;
  const roofBase = F + 5;
  // ground: foundation under the plot, clear air above (with the roof's overhang)
  for (let z = z0 - 1; z <= z1 + 1; z++)
    for (let x = x0 - 1; x <= x1 + 1; x++) {
      if (!b.inside(x, z)) continue;
      const inside = x >= x0 && x <= x1 && z >= z0 && z <= z1;
      for (let y = F + 1; y < F + 16; y++) b.set(x, y, z, B.air);
      if (inside) for (let y = b.fromY; y <= F; y++) if (b.get(x, y, z) === B.air || b.get(x, y, z) === B.water || y === F) b.set(x, y, z, y === F ? B.sprucePlanks : B.cobble);
    }
  for (let z = z0; z <= z1; z++)
    for (let x = x0; x <= x1; x++) {
      const edgeX = x === x0 || x === x1, edgeZ = z === z0 || z === z1;
      if (!edgeX && !edgeZ) continue;
      for (let y = F + 1; y <= F + 4; y++) {
        let id: number = y === F + 1 ? B.cobble : B.oakPlanks;
        if (edgeX && edgeZ) id = B.oakLog;
        // windows on the long walls and the east wall, a wide doorway west (towards the square)
        if (edgeZ && !edgeX && (y === F + 2 || y === F + 3) && ((x >= 29 && x <= 30) || (x >= 33 && x <= 34))) id = B.glass;
        if (x === x1 && !edgeZ && (y === F + 2 || y === F + 3) && z >= 18 && z <= 19) id = B.glass;
        if (x === x0 && !edgeZ && y <= F + 2 && z >= 18 && z <= 19) id = B.air;
        b.set(x, y, z, id);
      }
      // gable ends: planks up to the roof
      if (edgeX) {
        const k = Math.min(z - (z0 - 1), z1 + 1 - z);
        for (let y = F + 5; y < roofBase + k; y++) b.set(x, y, z, B.oakPlanks);
      }
    }
  // roof: spruce stairs rising from both long sides, a planks ridge on top
  for (let x = x0 - 1; x <= x1 + 1; x++)
    for (let k = 0; k <= 5; k++) {
      const zn = z0 - 1 + k, zs = z1 + 1 - k;
      if (k < 5) { b.set(x, roofBase + k, zn, B.roofS); b.set(x, roofBase + k, zs, B.roofN); }
      else { b.set(x, roofBase + k, zn, B.sprucePlanks); b.set(x, roofBase + k, zs, B.sprucePlanks); }
    }
  // chimney (the forge)
  for (let y = F + 1; y <= roofBase + 7; y++) b.set(35, y, 20, y >= roofBase + 6 ? B.mossy : B.cobble);
  for (let y = F + 1; y <= F + 2; y++) { b.set(34, y, 20, B.cobble); b.set(35, y, 21, B.cobble); }
  // yard: hay, a log pile, a fenced pen on the south side
  for (const [x, z, h] of [[29, 26, 2], [30, 26, 1], [29, 27, 1]] as const) for (let y = 1; y <= h; y++) b.set(x, F + y, z, B.hay);
  for (let x = 32; x <= 35; x++) { b.set(x, F + 1, 26, B.oakLogX); if (x < 35) b.set(x, F + 2, 26, B.oakLogX); }
  for (let x = 27; x <= 37; x++) b.set(x, F + 1, 29, B.fenceEW);
  for (let z = 25; z <= 29; z++) { b.set(26, F + 1, z, z === 29 ? B.fencePost : B.fenceNS); b.set(38, F + 1, z, z === 29 ? B.fencePost : B.fenceNS); }
  b.set(26, F + 1, 29, B.fencePost); b.set(38, F + 1, 29, B.fencePost);
}

function overlaps(b: Builder, x0: number, x1: number, z0: number, z1: number) {
  return x1 >= b.x0 && x0 < b.x0 + 16 && z1 >= b.z0 && z0 < b.z0 + 16;
}

/** square paving (stone bricks with a cobblestone border), worn path from the Smithy's door, building material */
function stampSquare(b: Builder, B: Ids, ground: (x: number, z: number) => number) {
  if (!overlaps(b, SQUARE.x0 - 1, SMITHY.x0, SQUARE.z0 - 1, SQUARE.z1 + 1)) return;
  for (let z = SQUARE.z0; z <= SQUARE.z1; z++)
    for (let x = SQUARE.x0; x <= SQUARE.x1; x++) {
      if (!b.inside(x, z)) continue;
      const edge = SQUARE.done + Math.floor(hash(0, z >> 1, 71) * 5) - 2;
      if (x > edge) continue;
      const g = ground(x, z);
      const border = x === SQUARE.x0 || z === SQUARE.z0 || z === SQUARE.z1;
      const id = border ? B.cobble : (x + z) % 7 === 0 ? B.andesite : B.stoneBricks;
      b.set(x, g, z, id);
      b.set(x, g + 1, z, B.air);
    }
  // material waiting at the edge of the paving
  const g = ground(SQUARE.done + 4, -4);
  for (const [x, z, h, id] of [[SQUARE.done + 4, -4, 2, B.stoneBricks], [SQUARE.done + 5, -4, 1, B.stoneBricks], [SQUARE.done + 4, -3, 1, B.cobble], [SQUARE.done + 6, 3, 1, B.cobble], [SQUARE.done + 6, 4, 1, B.gravel]] as const)
    for (let y = 1; y <= h; y++) b.set(x, g + y, z, id);
  // a worn path from the Smithy's door to the square
  for (let x = SQUARE.x1 + 1; x < SMITHY.x0; x++)
    for (const z of [18, 19]) {
      if (!b.inside(x, z)) continue;
      const gg = ground(x, z);
      if (hash(x, z, 73) < 0.85) b.set(x, gg, z, B.path);
      b.set(x, gg + 1, z, B.air);
    }
}

/**
 * The chunk at (cx, cz), as the world reader would decode it from a region file. Block states and biomes are
 * registered in the worker's registries.
 * @param opts.surface only the top few blocks of each column (enough for the map, a little quicker)
 * @param opts.fromY  make blocks from this height up at least (slice view: the cut shows what's below the surface)
 * @param opts.known  the blocks the loaded game file has (an older one may miss some; see `ids`)
 */
export function demoChunk(cx: number, cz: number, states: StateRegistry, biomes: NameRegistry, opts: { surface?: boolean; fromY?: number; known?: (name: string) => boolean } = {}): ChunkData {
  const B = ids(states, opts.known);
  const x0 = cx * 16, z0 = cz * 16;
  // columns with a one-block border (slopes, neighbours of water)
  const cols: Column[] = new Array(18 * 18);
  let minG = Infinity, maxG = -Infinity;
  for (let dz = -1; dz <= 16; dz++)
    for (let dx = -1; dx <= 16; dx++) {
      const c = column(x0 + dx, z0 + dz);
      cols[(dz + 1) * 18 + dx + 1] = c;
      // the border too: a cliff face at the chunk's edge goes down to the lower neighbour
      minG = Math.min(minG, c.g);
      if (dx >= 0 && dx < 16 && dz >= 0 && dz < 16) maxG = Math.max(maxG, c.g);
    }
  const col = (x: number, z: number) => cols[(z - z0 + 1) * 18 + (x - x0 + 1)];
  const ground = (x: number, z: number) => (Math.abs(x - x0 - 7.5) <= 9 && Math.abs(z - z0 - 7.5) <= 9 ? col(x, z).g : column(x, z).g);

  const fromY = Math.max(0, ((Math.min(minG, SEA, opts.fromY ?? Infinity) - (opts.surface ? 4 : 10)) >> 4) << 4);
  const toY = (((Math.max(maxG, SEA) + 26) >> 4) + 1) << 4;
  const b = new Builder(cx, cz, fromY, toY);

  // terrain
  for (let z = z0; z < z0 + 16; z++)
    for (let x = x0; x < x0 + 16; x++) {
      const c = col(x, z);
      const slope = Math.max(Math.abs(c.g - col(x + 1, z).g), Math.abs(c.g - col(x - 1, z).g), Math.abs(c.g - col(x, z + 1).g), Math.abs(c.g - col(x, z - 1).g));
      const top = topBlock(c, slope, B, x, z);
      const soil = top === B.sand ? B.sand : top === B.grass || top === B.grassSnowy || top === B.snowBlock ? B.dirt : top === B.gravel ? B.gravel : top === B.clay ? B.clay : -1;
      const soilDepth = 3 + Math.floor(hash(x, z, 81) * 2);
      for (let y = fromY; y <= c.g; y++) {
        const d = c.g - y;
        let id: number;
        if (d === 0) id = top;
        else if (d <= soilDepth && soil >= 0) id = soil === B.sand && d > 2 ? B.sandstone : soil;
        else id = rock(x, y, z, B);
        b.set(x, y, z, id);
      }
      for (let y = c.g + 1; y <= SEA; y++) b.set(x, y, z, B.water);
      // snow on the peaks
      if (c.g >= SEA + 66 && c.g >= SEA) b.set(x, c.g + 1, z, B.snow);
      if (top === B.snowBlock && c.g >= SEA + 82) b.set(x, c.g - 1, z, B.snowBlock);
      // plants
      if (top !== B.grass) {
        // sugar cane on sand next to the water
        if (top === B.sand && (c.g === SEA || c.g === SEA + 1) && hash(x, z, 83) < 0.2 && [col(x + 1, z), col(x - 1, z), col(x, z + 1), col(x, z - 1)].some((n) => n.g < SEA)) {
          const h = 1 + Math.floor(hash(x, z, 85) * 3);
          for (let y = 1; y <= h; y++) b.set(x, c.g + y, z, B.cane);
        }
        continue;
      }
      const r = hash(x, z, 87);
      const patch = fbm(x / 14, z / 14, 2, 89);
      const grassChance = c.biome === "plains" ? 0.26 : c.biome === "taiga" ? 0.1 : c.biome === "windswept_hills" ? 0.06 : 0.14;
      const flowerChance = (c.biome === "plains" ? 0.012 : c.biome === "forest" || c.biome === "birch_forest" ? 0.008 : 0.002) + (patch > 0.68 ? 0.12 : 0);
      if (r < flowerChance) {
        const f = hash(x >> 3, z >> 3, 91);
        b.set(x, c.g + 1, z, f < 0.35 ? B.dandelion : f < 0.65 ? B.poppy : f < 0.85 ? B.cornflower : B.daisy);
      } else if (r < flowerChance + grassChance) b.set(x, c.g + 1, z, B.shortGrass);
      else if (c.biome === "plains" && r > 0.99975 && patch > 0.5) b.set(x, c.g + 1, z, B.pumpkin);
      // the shore of the river: sugar cane on grass too
      else if ((c.g === SEA || c.g === SEA + 1) && hash(x, z, 93) < 0.18 && [col(x + 1, z), col(x - 1, z), col(x, z + 1), col(x, z - 1)].some((n) => n.g < SEA)) {
        const h = 1 + Math.floor(hash(x, z, 85) * 3);
        for (let y = 1; y <= h; y++) b.set(x, c.g + y, z, B.cane);
      }
    }

  // trees: one candidate per 5 × 5 cell, anchored somewhere in the cell
  const cx0 = Math.floor((x0 - 3) / TREE_CELL), cx1 = Math.floor((x0 + 18) / TREE_CELL);
  const cz0 = Math.floor((z0 - 3) / TREE_CELL), cz1 = Math.floor((z0 + 18) / TREE_CELL);
  for (let gz = cz0; gz <= cz1; gz++)
    for (let gx = cx0; gx <= cx1; gx++) {
      const t = treeIn(gx, gz);
      if (t) stampTree(b, B, t.x, t.z, t.g, t.kind);
    }

  stampSquare(b, B, ground);
  stampSmithy(b, B);

  return toChunk(b, cx, cz, cols, biomes);
}

const BIOME_IDS = new WeakMap<NameRegistry, Record<Biome, number>>();
/** state id → palette index while a section is packed (−1 = not in the palette yet) */
const LUT = new Int16Array(65536).fill(-1);


function toChunk(b: Builder, cx: number, cz: number, cols: Column[], biomes: NameRegistry): ChunkData {
  let bid = BIOME_IDS.get(biomes);
  if (!bid) {
    bid = {} as Record<Biome, number>;
    for (const n of BIOMES) bid[n] = biomes.id(`minecraft:${n}`);
    BIOME_IDS.set(biomes, bid);
  }
  // biomes per 4 × 4 cell (same at every height)
  const cellBiome: number[] = [];
  for (let qz = 0; qz < 4; qz++) for (let qx = 0; qx < 4; qx++) cellBiome.push(bid[cols[(qz * 4 + 2 + 1) * 18 + qx * 4 + 2 + 1].biome]);
  const bpal = [...new Set(cellBiome)];
  const bdata = new Uint16Array(64);
  for (let qy = 0; qy < 4; qy++) for (let q = 0; q < 16; q++) bdata[qy * 16 + q] = bpal.indexOf(cellBiome[q]);
  const biomesU = Uint32Array.from(bpal);

  const minSy = b.fromY >> 4;
  const n = (b.toY - b.fromY) >> 4;
  const sections: (Section | undefined)[] = new Array(n);
  for (let s = 0; s < n; s++) {
    const slice = b.buf.subarray(s * 4096, (s + 1) * 4096);
    const pal: number[] = [];
    const data = new Uint8Array(4096);
    for (let i = 0; i < 4096; i++) {
      const v = slice[i];
      let k = LUT[v];
      if (k < 0) { k = LUT[v] = pal.length; pal.push(v); }
      data[i] = k;
    }
    for (const v of pal) LUT[v] = -1;
    const st = Uint32Array.from(pal);
    sections[s] = { y: minSy + s, states: st, data: st.length > 1 ? data : null, biomes: biomesU, biomeData: bpal.length > 1 ? bdata : null };
  }
  const surface = new Int16Array(256), oceanFloor = new Int16Array(256);
  for (let z = 0; z < 16; z++)
    for (let x = 0; x < 16; x++) {
      const c = cols[(z + 1) * 18 + x + 1];
      oceanFloor[z * 16 + x] = c.g + 1;
      let y = b.toY - 1;
      while (y > b.fromY && b.buf[((y - b.fromY) * 16 + z) * 16 + x] === 0) y--;
      surface[z * 16 + x] = y + 1;
    }
  return { cx, cz, minSectionY: minSy, sections, surface, oceanFloor, status: "minecraft:full", decodedFromY: b.fromY };
}

/**
 * The island from above for its world picture: n × n samples (one per 1024 / n blocks) with the top block's state id,
 * its height and biome id — the ground, or the crown of a tree standing there. Much quicker than making every chunk.
 */
export function demoTopView(n: number, states: StateRegistry, biomes: NameRegistry): { ids: Uint32Array; heights: Int16Array; biomes: Uint32Array } {
  const B = ids(states);
  const out = { ids: new Uint32Array(n * n), heights: new Int16Array(n * n), biomes: new Uint32Array(n * n) };
  const step = 1024 / n;
  for (let j = 0; j < n; j++)
    for (let i = 0; i < n; i++) {
      const x = Math.floor(-512 + (i + 0.5) * step), z = Math.floor(-512 + (j + 0.5) * step);
      const c = column(x, z);
      let id = c.g < SEA ? B.water : topBlock(c, Math.max(...[[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dz]) => Math.abs(column(x + dx, z + dz).g - c.g))), B, x, z);
      let h = Math.max(c.g, SEA) + 1;
      // under a tree's crown?
      for (let gz = Math.floor((z - 3) / TREE_CELL); gz <= Math.floor((z + 3) / TREE_CELL); gz++)
        for (let gx = Math.floor((x - 3) / TREE_CELL); gx <= Math.floor((x + 3) / TREE_CELL); gx++) {
          const t = treeIn(gx, gz);
          if (!t || Math.abs(t.x - x) > (t.kind === "spruce" ? 1 : 2) || Math.abs(t.z - z) > (t.kind === "spruce" ? 1 : 2)) continue;
          id = t.kind === "oak" ? B.oakLeaves : t.kind === "birch" ? B.birchLeaves : B.spruceLeaves;
          h = Math.max(h, t.g + 6);
        }
      out.ids[j * n + i] = id;
      out.heights[j * n + i] = h;
      out.biomes[j * n + i] = biomes.id(`minecraft:${c.biome}`);
    }
  return out;
}

// ---------- the example plan ----------

/** An example plan object before it is placed on the ground (`y` comes from the terrain). */
export type DemoDraft = Pick<PlanObject, "kind" | "name" | "color" | "status" | "points"> & Partial<Pick<PlanObject, "width" | "height" | "material" | "notes">>;

function box(x: number, z: number, w: number, d: number): [number, number][] {
  return [[x, z], [x + w, z], [x + w, z + d], [x, z + d]];
}

/** A small village plan on the island: roads, a square, houses, fields, a castle idea. */
export const DEMO_DRAFTS: DemoDraft[] = [
  { kind: "path", name: "Main road", color: "#e8b04a", status: "planned", width: 4, material: "minecraft:dirt_path", points: [[-230, 70], [-140, 40], [-60, 12], [-18, 0], [40, -6], [110, -40], [190, -96]] },
  { kind: "area", name: "Market square", color: "#4ab0e8", status: "in_progress", points: box(-18, -16, 36, 32) },
  { kind: "building", name: "Town hall", color: "#e85a4a", status: "planned", height: 14, points: box(-12, -44, 24, 18) },
  { kind: "building", name: "Smithy", color: "#e8744a", status: "done", height: 8, points: box(26, 14, 12, 10) },
  { kind: "building", name: "Bakery", color: "#e8744a", status: "idea", height: 8, points: box(-40, 20, 11, 11) },
  { kind: "building", name: "House", color: "#d9534f", status: "planned", height: 9, points: box(52, -28, 10, 9) },
  { kind: "building", name: "House", color: "#d9534f", status: "planned", height: 9, points: box(70, -46, 10, 9) },
  { kind: "building", name: "House", color: "#d9534f", status: "idea", height: 9, points: box(-78, -8, 10, 9) },
  { kind: "building", name: "House", color: "#d9534f", status: "idea", height: 9, points: box(-100, 20, 10, 9) },
  { kind: "area", name: "Wheat fields", color: "#c9b33a", status: "planned", points: [[40, 36], [120, 30], [132, 92], [48, 100]] },
  { kind: "path", name: "Harbour pier", color: "#b98a54", status: "idea", width: 3, points: [[110, -40], [168, 12], [240, 58]] },
  { kind: "marker", name: "Castle on the hill", color: "#b04ae8", status: "idea", points: [[-150, -140]] },
];

/** Example plan objects placed on the ground (`ground(x, z)` = y of the top block there, the object sits on it). */
export function draftsToObjects(drafts: DemoDraft[], ground: (x: number, z: number) => number): PlanObject[] {
  const now = Date.UTC(2026, 8, 1);
  return drafts.map((d, i) => {
    const cx = d.points.reduce((a, p) => a + p[0], 0) / d.points.length;
    const cz = d.points.reduce((a, p) => a + p[1], 0) / d.points.length;
    return {
      id: `demo-${i}`,
      kind: d.kind,
      name: d.name,
      color: d.color,
      status: d.status,
      dimension: "minecraft:overworld",
      points: d.points,
      y: ground(cx, cz) + 1,
      width: d.width ?? (d.kind === "path" ? 3 : 0),
      height: d.height ?? 0,
      material: d.material,
      notes: d.notes ?? "",
      author: "MC Planner",
      createdAt: now,
      updatedAt: now,
    } satisfies PlanObject;
  });
}

/** The example plan on the generated island (the Smithy stands on its own floor). */
export function demoPlan(): PlanObject[] {
  return draftsToObjects(DEMO_DRAFTS, (x, z) => (x >= SMITHY.x0 && x <= SMITHY.x1 + 1 && z >= SMITHY.z0 && z <= SMITHY.z1 + 1 ? smithyY() : demoHeight(x, z) - 1));
}

/** Where the camera starts: the village, seen from the south-east. */
export const DEMO_SPAWN: [number, number, number] = [10, SEA + 6, 0];
